import { Clock, PhoneOff, MailCheck } from "lucide-react";

/**
 * NextStepsFooter — the universal "call-deflation" footer shown
 * at the end of every triage result. Sets clear time expectations
 * so residents don't anxiously phone their housing team.
 *
 * The `reviewDays` prop lets different flows set different timings.
 * The `teamName` prop lets copy adapt (e.g. "housing team" vs "lettings team").
 */
export default function NextStepsFooter({
  reviewDays = 28,
  teamName = "local housing team",
}) {
  return (
    <div
      className="bg-[#F3F8F5] border-l-4 border-[#00703C] rounded-md p-5 my-6"
      data-testid="next-steps-footer"
    >
      <h3 className="font-bold text-[#00703C] text-base mb-3 flex items-center gap-2">
        <MailCheck size={18} />
        What Happens Next
      </h3>

      <div className="space-y-3 text-sm text-gray-800">
        <p>
          Based on your answers, your summary appears structurally complete. Here is what to
          expect — and how to avoid unnecessary stress while you wait.
        </p>

        <div className="flex items-start gap-3 bg-white border border-gray-200 rounded p-3">
          <Clock size={18} className="text-[#00703C] mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-gray-900">
              Your {teamName} typically reviews these within {reviewDays} working days.
            </p>
            <p className="text-gray-600 text-xs mt-1">
              This is a careful, manual review — not something that can be rushed.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 bg-white border border-gray-200 rounded p-3">
          <PhoneOff size={18} className="text-[#00703C] mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-gray-900">
              Phoning before this date won't speed things up.
            </p>
            <p className="text-gray-600 text-xs mt-1">
              We recommend waiting for their direct written response. You've already done the
              important work here.
            </p>
          </div>
        </div>

        <p className="text-xs text-gray-600 italic pt-1">
          You're doing great. Having a clear summary like this means you'll walk into any
          conversation prepared and confident.
        </p>
      </div>
    </div>
  );
}
